import { Component, computed, inject, input } from '@angular/core';
import { NgClass } from '@angular/common';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { ThemeComponent } from '../../../theming';

export type ButtonSize = 'small' | 'default' | 'quadratic';
export type ButtonStyle =
  | 'primary'
  | 'secondary'
  | 'primary-alt'
  | 'secondary-alt'
  | 'error'
  | 'error-alt'
  | 'dark'
  | 'tertiary'
  | 'custom';

const DISABLED_FILLED =
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-standard-disabled disabled:border-standard-disabled disabled:text-standard-text-disabled';
const DISABLED_OUTLINED =
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-transparent disabled:border-standard-text-disabled disabled:text-standard-text-disabled';
const CMS_PRIMARY = `focus:outline-hidden bg-primary-main text-primary-on border-primary-main hover:bg-primary-hover hover:border-primary-hover ${DISABLED_FILLED}`;
const CMS_VARIANTS: Record<ButtonStyle, string> = {
  primary: CMS_PRIMARY,
  dark: CMS_PRIMARY,
  secondary: `focus:outline-hidden bg-secondary-main text-secondary-on border-secondary-dark hover:bg-secondary-hover hover:border-secondary-dark ${DISABLED_FILLED}`,
  'primary-alt': `focus:outline-hidden bg-transparent border-primary-main hover:bg-standard-hover-main ${DISABLED_OUTLINED}`,
  'secondary-alt': `focus:outline-hidden bg-transparent text-secondary-main border-secondary-main hover:bg-secondary-main/20 ${DISABLED_OUTLINED}`,
  error: `focus:outline-hidden bg-error-main text-error-on border-error-main hover:bg-error-hover hover:border-error-hover ${DISABLED_FILLED}`,
  'error-alt': `focus:outline-hidden bg-transparent text-error-main border-error-main hover:bg-error-main/20 hover:text-error-hover hover:border-error-hover ${DISABLED_OUTLINED}`,
  tertiary:
    'focus:outline-hidden bg-transparent text-standard-text-secondary border-transparent hover:bg-standard-hover-main enabled:hover:border-standard-text-secondary enabled:active:border-standard-text-secondary disabled:cursor-not-allowed disabled:border-transparent disabled:text-standard-text-disabled',
  custom: `focus:outline-none focus-visible:outline-1 focus-visible:outline-solid bg-transparent text-standard-text-primary border-transparent hover:bg-standard-hover-main ${DISABLED_OUTLINED}`,
};

const SHOP_DISABLED =
  'disabled:cursor-not-allowed disabled:text-standard-text-disabled';
const SHOP_FILLED = `${SHOP_DISABLED} disabled:bg-background-disabled disabled:border-gray-200`;
const SHOP_OUTLINED = `${SHOP_DISABLED} disabled:border-background-disabled`;
const PRIMARY_OUTLINE =
  '[color:var(--aw-primary-outline,var(--color-primary-main))] [border-color:var(--aw-primary-outline,var(--color-primary-main))]';
const SECONDARY_OUTLINE =
  '[color:var(--aw-secondary-outline,var(--color-secondary-main))] [border-color:var(--aw-secondary-outline,var(--color-secondary-main))]';
const SHOP_VARIANTS: Record<ButtonStyle, string> = {
  primary: `bg-primary-main text-primary-on border-primary-main ${SHOP_FILLED}`,
  secondary: `bg-secondary-main text-secondary-on border-secondary-main ${SHOP_FILLED}`,
  'primary-alt': `bg-transparent ${PRIMARY_OUTLINE} ${SHOP_OUTLINED}`,
  'secondary-alt': `bg-transparent ${SECONDARY_OUTLINE} ${SHOP_OUTLINED}`,
  error: `bg-error-main text-error-on border-error-main ${SHOP_FILLED}`,
  'error-alt': `bg-transparent text-error-main border-error-main ${SHOP_OUTLINED}`,
  tertiary: `bg-transparent [color:var(--aw-primary-outline,var(--color-primary-main))] border-transparent ${SHOP_OUTLINED}`,
  custom: `bg-transparent border-gray-200 data-[mode=dark]:enabled:text-standard-text-dark ${SHOP_FILLED}`,
  dark: `bg-transparent border-gray-200 ${SHOP_FILLED}`,
};

@Component({
  selector: 'aw-button',
  imports: [NgClass, AppendTestIdPipe],
  templateUrl: './button.component.html',
  styleUrl: './button.component.css',
})
export class ButtonComponent {
  readonly id = input.required<string>();
  readonly text = input<string>();
  readonly type = input.required<'submit' | 'button' | 'reset'>();
  readonly disabledText = input('Button disabled');
  readonly isDisabled = input<boolean>();
  /** Retained for API compatibility; the existing button always occupies full width. */
  readonly isFullWidth = input(true);
  readonly style = input<ButtonStyle>('primary');
  readonly buttonSize = input<ButtonSize>('default');
  readonly isModalButton = input(false);
  readonly flattenRightSide = input(false);
  readonly flattenLeftSide = input(false);

  private readonly theme = inject(ThemeComponent);
  protected readonly context = this.theme.context;
  protected readonly mode = this.theme.resolvedMode;

  protected readonly variantClasses = computed(
    () =>
      (this.context() === 'shop' ? SHOP_VARIANTS : CMS_VARIANTS)[this.style()]
  );
}
